import {
  resolvePlayerLoadingSrc,
  readCachedPlayerLoadingSrc,
  rememberPlayerLoadingSrc,
  paintHtmlBootLoadingImage,
  subscribePlayerLoading,
  isPlayerLoadingVisible,
  beginPlayerLoading,
  endPlayerLoading,
  BLOCKING_LOAD_STUCK_MS,
  getGameSession,
  markGameFrameReady,
  closeGameSession,
  failGameSession,
  isUnusableGameFrame,
  isTrustedGameFrameMessage,
  GAME_LAUNCH_FAILED_MESSAGE,
  markPlayerBootReady,
} from './playerLoading.js';

export function createPlayerLoadingUi({
  useSyncExternalStore,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  useAuth,
  useSiteConfig,
  jsx,
  jsxs,
}) {
  const layout = useLayoutEffect || useEffect;

  function BlockingLoadGate({ active }) {
    layout(() => {
      if (!active) return undefined;
      const id = beginPlayerLoading('route-gate', BLOCKING_LOAD_STUCK_MS);
      return () => endPlayerLoading(id);
    }, [active]);
    return null;
  }

  function GameCancelButton() {
    return jsx('button', {
      type: 'button',
      className: 'ninet6-game-close',
      'aria-label': 'Close',
      onClick: () => closeGameSession(),
      children: '✕',
    });
  }

  function PlayerLoadingHost() {
    const { sessionReady } = useAuth();
    const tokenVisible = useSyncExternalStore(subscribePlayerLoading, isPlayerLoadingVisible, isPlayerLoadingVisible);
    const session = useSyncExternalStore(subscribePlayerLoading, getGameSession, getGameSession);
    const site = useSiteConfig ? useSiteConfig() : { branding: {}, brandingVersion: '' };
    const logoSrc = resolvePlayerLoadingSrc(site?.branding?.loadingImage?.url, site?.brandingVersion)
      || readCachedPlayerLoadingSrc();
    const [imgSrc, setImgSrc] = useState(logoSrc);

    layout(() => {
      if (sessionReady) markPlayerBootReady();
    }, [sessionReady]);

    useEffect(() => {
      setImgSrc(logoSrc);
      if (logoSrc) {
        rememberPlayerLoadingSrc(site?.branding?.loadingImage?.url);
        paintHtmlBootLoadingImage(logoSrc);
      }
    }, [logoSrc, site?.branding?.loadingImage?.url]);

    const htmlSplash = typeof document !== 'undefined' && document.getElementById('ninet6-boot-loader');
    const visible = !sessionReady || tokenVisible;
    if (!visible) return null;
    if (!sessionReady && htmlSplash) return null;
    const canCancel = !!(session.loadingToken || session.url) && !session.frameReady;
    return jsxs('div', {
      className: 'ninet6-global-loader',
      id: 'ninet6-global-loader',
      role: 'progressbar',
      'aria-busy': 'true',
      'aria-label': 'Loading',
      children: [
        imgSrc
          ? jsx('img', {
            className: 'ninet6-global-loader-img',
            src: imgSrc,
            alt: '',
            onError: () => setImgSrc(''),
          })
          : null,
        jsx('div', {
          className: imgSrc ? 'ninet6-global-loader-spinner is-secondary' : 'ninet6-global-loader-spinner',
          'aria-hidden': 'true',
        }),
        canCancel ? jsx(GameCancelButton, {}) : null,
      ],
    });
  }

  function GamePlayHost() {
    const session = useSyncExternalStore(subscribePlayerLoading, getGameSession, getGameSession);
    const frameRef = useRef(null);
    const acceptedKey = useRef('');

    const onReady = () => {
      const current = getGameSession();
      if (!current.url || current.frameReady) return;
      const key = `${current.loadingToken}:${current.url}`;
      if (acceptedKey.current === key) return;
      acceptedKey.current = key;
      markGameFrameReady();
      endPlayerLoading(current.loadingToken);
    };

    const onFrameLoad = () => {
      if (isUnusableGameFrame(frameRef.current)) return;
      onReady();
    };

    const onFail = () => {
      const msg = failGameSession(GAME_LAUNCH_FAILED_MESSAGE);
      try {
        window.alert(msg);
      } catch {
        /* ignore */
      }
    };

    useEffect(() => {
      acceptedKey.current = '';
    }, [session.url, session.loadingToken]);

    useEffect(() => {
      const el = frameRef.current;
      if (!el || !session.url || session.frameReady) return undefined;
      const finish = () => {
        if (isUnusableGameFrame(el)) return;
        onReady();
      };
      el.addEventListener('load', finish);
      return () => el.removeEventListener('load', finish);
    }, [session.url, session.frameReady, session.loadingToken]);

    useEffect(() => {
      if (!session.url || session.frameReady) return undefined;
      const onMessage = (ev) => {
        if (!isTrustedGameFrameMessage(ev, frameRef.current)) return;
        onReady();
      };
      window.addEventListener('message', onMessage);
      return () => window.removeEventListener('message', onMessage);
    }, [session.url, session.frameReady, session.loadingToken]);

    if (!session.url && !session.loadingToken) return null;

    return jsxs('div', {
      className: session.frameReady ? 'ninet6-game-layer is-ready' : 'ninet6-game-layer',
      children: [
        session.url
          ? jsx('iframe', {
            className: 'ninet6-game-frame',
            title: 'Game',
            src: session.url,
            ref: frameRef,
            allowFullScreen: true,
            allow: 'fullscreen; autoplay; clipboard-write; payment; gyroscope; accelerometer',
            referrerPolicy: 'origin-when-cross-origin',
            onLoad: onFrameLoad,
            onError: onFail,
          })
          : null,
        session.frameReady ? jsx(GameCancelButton, {}) : null,
      ],
    });
  }

  return { PlayerLoadingHost, GamePlayHost, BlockingLoadGate };
}
